import { Component, OnInit, OnDestroy, HostListener, ViewChild, Host } from '@angular/core';
import { Subscription } from 'rxjs';
import { SidebarService, ISidebar } from '../sidebar/sidebar.service';
import { Router } from '@angular/router';
import { LangService, Language } from 'src/app/shared/lang.service';
import { AuthService } from 'src/app/shared/auth.service';
import { environment } from 'src/environments/environment';
import { ImpersonateModalComponent } from 'src/app/containers/applications/impersonate-modal/impersonate-modal.component';
import { FirestoreService } from '../../../data/firestore.service';
import { User } from 'src/app/data/types';

@Component({
    selector: 'app-topnav',
    templateUrl: './topnav.component.html',
    standalone: false
})

export class TopnavComponent implements OnInit, OnDestroy {
  sidebar: ISidebar;
  subscription: Subscription;
  displayName = 'Guest';
  initials = 'G';
  languages: Language[];
  currentLanguage: string;
  isSingleLang;
  isFullScreen = false;
  isDarkSwitchActive = environment.isDarkSwitchActive;
  isDarkModeActive = false;
  isSearchActive = false;
  searchKey = '';
  canImpersonate = false;
  isImpersonating = false;
  notifications = [];
  unreadNotifications = 0;
  nbDaysReadNotifications = 14;

  @ViewChild('impersonateModalRef', { static: true }) impersonateModalRef: ImpersonateModalComponent;

  constructor(private sidebarService: SidebarService, private authService: AuthService, private router: Router, private langService: LangService, private db: FirestoreService) {
    this.languages = this.langService.supportedLanguages;
    this.currentLanguage = this.langService.languageShorthand;
    this.isSingleLang = this.langService.isSingleLang;
    this.isDarkModeActive = this.getColor().indexOf('dark') > -1 ? true : false;
  }

  onDarkModeChange(event) {
    let color = this.getColor();
    if (color.indexOf('dark') > -1) {
      color = color.replace('dark', 'light');
    } else if (color.indexOf('light') > -1) {
      color = color.replace('light', 'dark');
    }
    localStorage.setItem(environment.themeColorStorageKey, color);
    setTimeout(() => {
      window.location.reload();
    }, 200);
  }

  getColor() {
    return localStorage.getItem(environment.themeColorStorageKey)
      ? localStorage.getItem(environment.themeColorStorageKey)
      : environment.defaultColor;
  }

  fullScreenClick() {
    this.isFullScreen = !this.isFullScreen;
    if (this.isFullScreen) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }

  onLanguageChange(lang) {
    this.langService.language = lang.code;
    this.currentLanguage = this.langService.languageShorthand;
  }

  async ngOnInit() {
    const self = this;
    const user = this.authService.user;

    if (user) {
      // Only self-signup sets a displayName on the Firebase Auth record (auth.service
      // register()); every other flow — admin creation, invites, renames in manage/user —
      // writes the users document instead. So Auth is only a first-paint stand-in here,
      // and the profile document below is the authoritative name.
      this.setDisplayName(user.displayName || user.email?.split('@')[0]);

      this.db.get<User>('users', user.uid).subscribe(data =>{
        if(data){
          this.unreadNotifications = data.unreadNotifications;
          this.setDisplayName(data.displayName);
        }
      });

      this.db.list('notifications', [['read', '==', false]], `/users/${user.uid}`).subscribe(updateNotifications);
      const notifDate = new Date();
      notifDate.setUTCHours(notifDate.getUTCHours() - this.nbDaysReadNotifications*24);
      this.db.list('notifications', [['created', '>=', notifDate]], `/users/${user.uid}`).subscribe(updateNotifications);
    }

    this.subscription = this.sidebarService.getSidebar().subscribe(
      res => {
        this.sidebar = res;
      },
      err => {
        console.error(`An error occurred: ${err.message}`);
      }
    );

    this.canImpersonate = await this.authService.hasRole('admin');
    this.isImpersonating = await this.authService.isImpersonating();

    function updateNotifications(notifications){
      notifications.forEach(notification =>{
        const existingIndex = self.notifications.findIndex(existingNotification =>{
          return existingNotification.id === notification.id;
        });
        if(existingIndex === -1){
          self.notifications.push(notification);
          return;
        }
        self.notifications[existingIndex] = notification;
      });
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private setDisplayName(name: string | null | undefined) {
    const trimmed = (name || '').trim();
    if (!trimmed) {
      return;
    }
    this.displayName = trimmed;
    this.initials = trimmed.split(' ').filter(word => word.length > 0).map(word => word[0]).join('');
  }

  menuButtonClick = (e: { stopPropagation: () => void; }, menuClickCount: number, containerClassnames: string) => {
    if (e) { e.stopPropagation(); }

    setTimeout(() => {
      const event = document.createEvent('HTMLEvents');
      event.initEvent('resize', false, false);
      window.dispatchEvent(event);
    }, 350);

    this.sidebarService.setContainerClassnames(
      ++menuClickCount,
      containerClassnames,
      this.sidebar.selectedMenuHasSubItems
    );
  }

  mobileMenuButtonClick = (event: { stopPropagation: () => void; }, containerClassnames: string) => {
    if (event) { event.stopPropagation(); }
    this.sidebarService.clickOnMobileMenu(containerClassnames);
  }

  onSignOut() {
    this.authService.signOut().subscribe(() => {
      this.router.navigate(['/signed-out']);
    });
  }

  onImpersonate(){
    this.impersonateModalRef.show();
  }

  async onUnimpersonate(){
    try{
      await this.authService.unimpersonate();
      window.location.reload();
    }
    catch(err){
      console.error(err);
    }
  }

  async onImpersonateSubmitted(userId){
    try{
      await this.authService.impersonate(userId);
      window.location.reload();
    }
    catch(err){
      console.error(err);
    }
  }

  async readNotifications(): Promise<void>{
    await Promise.all(this.notifications.map(async (notification) =>{
      if(!notification.read){
        notification.read = true;
        this.unreadNotifications--;
        await this.db.upsert('notifications', {...notification}, `/users/${this.authService.user.uid}`);
      }
    }));
  }

  searchKeyUp(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.search();
    } else if (event.key === 'Escape') {
      const input = document.querySelector('.mobile-view');
      if (input && input.classList) { input.classList.remove('mobile-view'); }
      this.searchKey = '';
    }
  }

  searchAreaClick(event) {
    event.stopPropagation();
  }
  searchClick(event) {
    if (window.innerWidth < environment.menuHiddenBreakpoint) {
      let elem = event.target;
      if (!event.target.classList.contains('search')) {
        if (event.target.parentElement.classList.contains('search')) {
          elem = event.target.parentElement;
        } else if (
          event.target.parentElement.parentElement.classList.contains('search')
        ) {
          elem = event.target.parentElement.parentElement;
        }
      }

      if (elem.classList.contains('mobile-view')) {
        this.search();
        elem.classList.remove('mobile-view');
      } else {
        elem.classList.add('mobile-view');
      }
    } else {
      this.search();
    }
    event.stopPropagation();
  }

  search() {
    if (this.searchKey && this.searchKey.length > 1) {
      this.router.navigate(['/app/pages/miscellaneous/search'], { queryParams: { key: this.searchKey.toLowerCase().trim() } });
      this.searchKey = '';
    }
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event) {
    const input = document.querySelector('.mobile-view');
    if (input && input.classList) { input.classList.remove('mobile-view'); }
    this.searchKey = '';
  }
}
